import { normalizeColor } from "../util/";
import { CreateElement } from "./element";
import { CreateRpr, OptionsRpr } from "./rpr";

interface OptionsParagraph extends OptionsRpr {
    /** Alignment */
    align?: string;
    /** Background color: HEX value */
    backgroundColor?: string;
    /** Bidi */
    bidi?: boolean;
    /** Contextual spacing. Don't added space between paragraphs of the same style */
    contextualSpacing?: boolean;
    /** Indentations: twips value */
    indentation?: {
        firstLine?: number;
        hanging?: number;
        left?: number;
        right?: number;
    };
    /** Keep lines together */
    keepLines?: boolean;
    /** Keep with next */
    keepNext?: boolean;
    /** Spacing: twips value */
    spacing?: {
        line?: number;
        after?: number;
        before?: number;
    };
    /** Outline level (heading level): 1, 2, 3... */
    outlineLvl?: number;
    /** Paragraph style */
    pStyle?: string;
    /** Parse line breaks. Default as true */
    parseLineBreaks?: boolean;
    /** Parse tabs. Default as true */
    parseTabs?: boolean;
    /** Parse break before */
    pageBreakBefore?: boolean;
    /** RTL */
    rtl?: boolean;
    /** Shading color: HEX value */
    shading?: string;
}

/**
 * Create paragraph
 */
class CreateParagraph extends CreateElement {
    /**
     * Element options
     */
    protected options: OptionsParagraph;

    /**
     * Constructor
     *
     * @param options - Options paragraph
     */
    constructor(options: OptionsParagraph) {
        super();

        // default options
        options.parseLineBreaks ??= true;
        options.parseTabs ??= true;

        this.options = options;
    }

    /**
     * Creates content
     *
     * @param contentsParagraph - Paragraph contents
     * @returns Paragraph
     */
    public create(contentsParagraph: string = ""): string {
        let contents = "<w:p>";

        // pPr styles
        contents += this.createPpr();

        contents += contentsParagraph;

        contents += "</w:p>";

        // replace line breaks
        if (
            this.options.parseLineBreaks != null &&
            this.options.parseLineBreaks
        ) {
            contents = contents.replace(
                /\n\r|\n|\r\n|\r/g,
                '</w:t><w:br/><w:t xml:space="preserve">'
            );
        }

        // replace tabs
        if (this.options.parseTabs != null && this.options.parseTabs) {
            contents = contents.replace(
                /\t/g,
                '</w:t><w:tab/><w:t xml:space="preserve">'
            );
        }

        return contents;
    }

    /**
     * Create pPr Styles
     *
     * @returns Styles
     */
    public createPpr(): string {
        let contents = "<w:pPr>";

        if (this.options.pStyle != null) {
            contents += '<w:pStyle w:val="' + this.options.pStyle + '"/>';
        }

        if (this.options.align != null) {
            if (this.options.align == "justify") {
                // normalize values
                this.options.align = "both";
            }
            contents += '<w:jc w:val="' + this.options.align + '" />';
        }

        if (this.options.bidi != null) {
            contents += '<w:bidi w:val="' + this.options.bidi + '"/>';
        }

        if (this.options.contextualSpacing != null) {
            contents +=
                '<w:contextualSpacing w:val="' +
                this.options.contextualSpacing +
                '"/>';
        }

        if (this.options.backgroundColor != null) {
            contents +=
                '<w:shd w:fill="' +
                this.options.backgroundColor +
                '" w:val="clear"/>';
        }

        if (this.options.indentation != null) {
            const indentations = this.options.indentation;

            contents += "<w:ind ";
            if (indentations.firstLine != null) {
                contents += ' w:firstLine="' + indentations.firstLine + '" ';
            }
            if (indentations.hanging != null) {
                contents += ' w:hanging="' + indentations.hanging + '" ';
            }
            if (indentations.left != null) {
                contents += ' w:left="' + indentations.left + '" ';
            }
            if (indentations.right != null) {
                contents += ' w:right="' + indentations.right + '" ';
            }
            contents += "/>";
        }

        if (this.options.keepLines != null) {
            contents += '<w:keepLines w:val="' + this.options.keepLines + '"/>';
        }

        if (this.options.keepNext != null) {
            contents += '<w:keepNext w:val="' + this.options.keepNext + '"/>';
        }

        if (this.options.spacing != null) {
            const spacings = this.options.spacing;

            contents += "<w:spacing ";
            if (spacings.line != null) {
                contents += ' w:line="' + spacings.line + '" ';
            }
            if (spacings.after != null) {
                contents += ' w:after="' + spacings.after + '" ';
            }
            if (spacings.before != null) {
                contents += ' w:before="' + spacings.before + '" ';
            }
            contents += ' w:lineRule="auto" />';
        }

        if (this.options.outlineLvl != null) {
            contents +=
                '<w:outlineLvl w:val="' + this.options.outlineLvl + '"/>';
        }

        if (this.options.pageBreakBefore != null) {
            contents +=
                '<w:pageBreakBefore w:val="' +
                this.options.pageBreakBefore +
                '"/>';
        }

        if (this.options.rtl != null) {
            contents += '<w:rtl w:val="' + this.options.rtl + '"/>';
        }

        if (this.options.shading != null) {
            contents +=
                '<w:shd w:color="auto" w:fill="' +
                normalizeColor(this.options.shading) +
                '" w:val="clear"/>';
        }

        // rPr styles
        const rPrElement = new CreateRpr(this.options);
        contents += rPrElement.create();

        contents += "</w:pPr>";

        return contents;
    }
}

export { CreateParagraph, OptionsParagraph };
